/**
 * BestOpportunityCard
 *
 * Featured card shown at the top of the patient suggestions drawer.
 * Highlights the single best slot with date, time, professional, reasons,
 * and primary/secondary CTAs.
 */
import React from 'react';
import { Button } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { getCompatibilityLabel, getScoreReasons } from '../utils/relevanceEngine';

const MONTHS_PT = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];
const WEEKDAYS_PT = [
  'domingo', 'segunda-feira', 'terça-feira', 'quarta-feira',
  'quinta-feira', 'sexta-feira', 'sábado',
];

function parseDate(dateStr) {
  if (!dateStr) return null;
  try {
    const d = new Date(`${dateStr}T12:00:00`);
    if (isNaN(d.getTime())) return null;
    return {
      weekday: WEEKDAYS_PT[d.getDay()],
      day: d.getDate(),
      month: MONTHS_PT[d.getMonth()],
      year: d.getFullYear(),
    };
  } catch {
    return null;
  }
}

function BestOpportunityCard({
  slot,
  onOffer,
  onSchedule,
  offered = false,
  onAccepted,
  onDenied,
  onResend,
}) {
  if (!slot) return null;

  const parsed = parseDate(slot.slotDate);
  const compat = getCompatibilityLabel(slot.compatibilityScore || 0);
  const reasons = getScoreReasons({
    preferredProfessional: !!slot.resourceLabel,
    preferredTime: false,
    waitingDays: slot.waitingDays || 0,
    priority: slot.priority,
    daysUntilSlot: slot.slotDate
      ? Math.max(0, Math.floor((new Date(`${slot.slotDate}T12:00:00`) - Date.now()) / 86400000))
      : 0,
  });

  return (
    <div style={{
      margin: '0 0 16px 0',
      padding: '16px',
      background: '#fff',
      border: '1.5px solid #e2e8f0',
      borderRadius: '12px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    }}>
      {/* Header: label + compatibility badge */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
          Melhor oportunidade
        </span>
        <span style={{
          fontSize: '0.72rem',
          fontWeight: 600,
          color: compat.color,
          background: `${compat.color}12`,
          padding: '3px 10px',
          borderRadius: '20px',
          border: `1px solid ${compat.color}25`,
          whiteSpace: 'nowrap',
        }}>
          {compat.label}
        </span>
      </div>

      {/* Date */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', marginBottom: 1 }}>
          {parsed ? `${parsed.day} de ${parsed.month} de ${parsed.year}` : slot.slotDate}
        </div>
        {parsed?.weekday && (
          <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{parsed.weekday}</div>
        )}
      </div>

      {/* Time + Professional */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 12 }}>
        {slot.slotTime && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <ScheduleIcon sx={{ fontSize: '0.9rem', color: '#94a3b8', flexShrink: 0 }} />
            <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#334155' }}>{slot.slotTime}</span>
          </div>
        )}
        {slot.resourceLabel && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <PersonOutlineIcon sx={{ fontSize: '0.9rem', color: '#94a3b8', flexShrink: 0 }} />
            <span style={{ fontSize: '0.82rem', color: '#475569' }}>{slot.resourceLabel}</span>
          </div>
        )}
      </div>

      {/* Reasons */}
      {reasons.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 14 }}>
          {reasons.map((r) => (
            <span key={r} style={{
              fontSize: '0.7rem',
              color: '#475569',
              background: '#f1f5f9',
              padding: '3px 9px',
              borderRadius: '20px',
              fontWeight: 500,
            }}>
              {r}
            </span>
          ))}
        </div>
      )}

      {/* CTAs */}
      {!offered ? (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<WhatsAppIcon sx={{ fontSize: '1rem' }} />}
            onClick={onOffer}
            sx={{
              background: '#16a34a',
              color: '#fff',
              fontSize: '0.82rem',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              boxShadow: 'none',
              flex: 1,
              padding: '7px 12px',
              '&:hover': { background: '#15803d' },
            }}
          >
            Oferecer horário
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={onSchedule}
            sx={{
              fontSize: '0.82rem',
              textTransform: 'none',
              borderColor: '#cbd5e1',
              color: '#334155',
              borderRadius: '8px',
              fontWeight: 600,
              padding: '7px 14px',
              '&:hover': { borderColor: '#94a3b8', background: '#f8fafc' },
            }}
          >
            Agendar
          </Button>
        </div>
      ) : (
        /* Post-offer state */
        <div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            padding: '8px 12px',
            background: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: '8px',
            marginBottom: 10,
          }}>
            <CheckCircleIcon sx={{ fontSize: '1rem', color: '#16a34a' }} />
            <span style={{ fontSize: '0.78rem', color: '#15803d', fontWeight: 500 }}>
              Oferta enviada — aguardando resposta
            </span>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="small"
              onClick={onAccepted}
              sx={{
                background: '#e11d48',
                color: '#fff',
                fontSize: '0.78rem',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '8px',
                boxShadow: 'none',
                '&:hover': { background: '#be123c' },
              }}
            >
              Paciente aceitou
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={onDenied}
              sx={{
                fontSize: '0.78rem',
                textTransform: 'none',
                borderColor: '#e2e8f0',
                color: '#64748b',
                borderRadius: '8px',
                fontWeight: 500,
              }}
            >
              Paciente negou
            </Button>
            <Button
              variant="text"
              size="small"
              startIcon={<WhatsAppIcon sx={{ fontSize: '0.9rem' }} />}
              onClick={onResend}
              sx={{
                fontSize: '0.78rem',
                textTransform: 'none',
                color: '#64748b',
                borderRadius: '8px',
              }}
            >
              Reenviar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default BestOpportunityCard;
